import React from 'react';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { hasSupervisorFeatureFlag } from '@paragon/login-tools';

import {
  addComponentDesignsToAssemblyGroup,
  editAssemblyGroup,
  removeComponentDesignsFromAssemblyGroup,
  updateComponentDesignQuantityInAssemblyGroup,
} from '../../actions/assemblyGroupActions';
import { showEditAssemblyGroupPropertiesDialog, showSendAssemblyGroupDialog } from '../../actions/dialogActions';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ResponsiveButtonBar } from '../interface/ResponsiveButtonBar';
import { ErrorPage } from './ErrorPage';
import { ComponentDesignsList } from './StationPage/ComponentDesignsList';

export function AssemblyGroupPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { assemblyGroupGuid } = useParams();

  const assemblyGroupsState = useAppSelector((state) => state.assemblyGroups);
  const isSupervisor = useAppSelector(hasSupervisorFeatureFlag);

  const assemblyGroup = assemblyGroupGuid ? assemblyGroupsState.data[assemblyGroupGuid] : null;

  if (assemblyGroupsState.status === 'loading') return null;
  if (!assemblyGroup) return <ErrorPage body="That assembly group can not be found." />;

  return (
    <>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ marginRight: 'auto' }}>
          <Stack direction="row" sx={{ alignItems: 'center' }}>
            <WarehouseIcon fontSize="inherit" sx={{ marginRight: 1 }} />
            {assemblyGroup.name}
          </Stack>
        </Typography>

        {isSupervisor && (
          <Box sx={{ ml: 1 }}>
            <ResponsiveButtonBar
              breakpoint="md"
              actions={[
                ['Send to Lines/Stations', () => void dispatch(showSendAssemblyGroupDialog(assemblyGroup.guid))],
                ['Edit Properties', () => void dispatch(showEditAssemblyGroupPropertiesDialog(assemblyGroup.guid))],
                [
                  'Archive',
                  () => {
                    navigate('/assembly-groups');
                    dispatch(editAssemblyGroup(assemblyGroup.guid, { archived: true }));
                  },
                ],
              ]}
            />
          </Box>
        )}
      </Stack>

      {assemblyGroup.notes && (
        <Paper sx={{ padding: 2 }}>
          <Typography>Notes: {assemblyGroup.notes}</Typography>
        </Paper>
      )}

      <ComponentDesignsList
        context="assembly group"
        contextGuid={assemblyGroup.guid}
        componentDesigns={assemblyGroup.componentDesigns}
        onUploadComponentDesigns={(uploadFiles) =>
          dispatch(addComponentDesignsToAssemblyGroup(assemblyGroup.guid, uploadFiles))
        }
        onUpdateComponentDesignQuantity={(componentDesignGuid, quantity) => {
          dispatch(updateComponentDesignQuantityInAssemblyGroup(assemblyGroup.guid, componentDesignGuid, quantity));
        }}
        onClearComponentDesigns={() => {
          dispatch(
            removeComponentDesignsFromAssemblyGroup(assemblyGroup.guid, Object.keys(assemblyGroup.componentDesigns)),
          );
        }}
        onRemoveComponentDesign={(componentDesignGuid: string) => {
          dispatch(removeComponentDesignsFromAssemblyGroup(assemblyGroup.guid, componentDesignGuid));
        }}
      />
    </>
  );
}
